import { useCallback, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box } from "@mui/system";
import useIsMobile from "../hooks/useIsMobile";
import ThreeDotsActiveButton from "./ThreeDotsActiveButton";

export type ConfirmationState = {
  openDialog: (
    confirmCallback: () => void,
    cancelCallback?: () => void,
  ) => void;
  closeDialog: () => void;
  dialogProps: {
    isOpen: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    loading?: boolean;
  };
  setLoading: (loading: boolean) => void;
};

export const useConfirmationDialog = (): ConfirmationState => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [onConfirm, setOnConfirm] = useState<(() => void) | null>(null);
  const [onCancel, setOnCancel] = useState<(() => void) | null>(null);

  const openDialog = useCallback(
    (confirmCallback: () => void, cancelCallback?: () => void) => {
      setIsOpen(true);
      setLoading(false);
      setOnConfirm(() => confirmCallback);
      setOnCancel(() => cancelCallback);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setLoading(false);
  }, []);

  const confirm = useCallback(async () => {
    if (onConfirm) {
      try {
        await onConfirm();
      } catch (error) {
        console.error("Confirmation action failed:", error);
      }
    }
    closeDialog();
  }, [onConfirm, closeDialog]);

  const cancel = useCallback(() => {
    if (onCancel) onCancel();
    closeDialog();
  }, [onCancel, closeDialog]);

  return {
    openDialog,
    closeDialog,
    setLoading,
    dialogProps: {
      isOpen,
      onConfirm: confirm,
      onCancel: cancel,
      loading,
    },
  };
};

type Props = {
  isOpen: boolean;
  fullScreen?: boolean;
  // Passed in by state.
  title?: string;
  content?: string;
  onConfirm: () => void;
  onCancel: () => void;
  noIcon?: boolean;
  loading?: boolean;
  yesButtonText?: string;
  noButtonText?: string;
  hideCancelButton?: boolean;
};

export default ({
  title = "Confirmation",
  content = "Are you sure you want to proceed",
  isOpen,
  onConfirm,
  onCancel,
  fullScreen,
  noIcon = true,
  loading = false,
  yesButtonText = "Yes",
  noButtonText = "No",
  hideCancelButton = false,
}: Props) => {
  const isMobile = useIsMobile();
  // Defer to an explicit fullScreen prop if it's provided
  const isFullScreen = fullScreen === undefined ? isMobile : fullScreen;
  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="sm"
      fullScreen={isFullScreen}
      PaperComponent={Paper}
      PaperProps={{
        style: {
          ...(isFullScreen
            ? {
                boxShadow: "none",
              }
            : {
                borderRadius: "12px",
                backgroundColor: "var(--base-white, #FFF)",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                border: "1px solid #EAECF0",
              }),
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: "600", padding: "16px 24px 12px" }}
        id="confirmation-dialog-title"
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ padding: "0 24px" }}>
        <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
          {!noIcon && (
            <WarningAmberIcon
              sx={{
                height: "36px",
                width: "36px",
              }}
            />
          )}
          <DialogContentText
            sx={{ fontSize: "16px" }}
            id="confirmation-dialog-description"
          >
            {content}
          </DialogContentText>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        {!hideCancelButton && (
          <Button
            variant="text"
            fullWidth={isFullScreen}
            onClick={onCancel}
            disabled={loading}
          >
            {noButtonText}
          </Button>
        )}
        <ThreeDotsActiveButton
          fullWidth={isFullScreen}
          onClick={onConfirm}
          autoFocus
          disabled={loading}
          active={loading}
        >
          {yesButtonText}
        </ThreeDotsActiveButton>
      </DialogActions>
    </Dialog>
  );
};
